import React from 'react';
import {
  FieldErrorProps as AriaFieldErrorProps,
  FieldError as AriaFieldError,
  ValidationResult,
} from 'react-aria-components';
import { Icon } from '../Icon/Icon';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden';
import styles from './FieldError.module.css';

export interface FieldErrorProps
  extends Omit<AriaFieldErrorProps, 'children' | 'className'> {
  /**
   * Error message content
   * Can be a static string or a function that returns an error message
   */
  children?: React.ReactNode | ((validation: ValidationResult) => string);

  /**
   * Prefix text for screen readers
   * @default 'Error:'
   */
  errorPrefix?: string;
  /**
   * Label of the field to include in the error prefix
   */
  fieldLabel?: string;

  /**
   * Explicitly mark the field as invalid
   */
  isInvalid?: boolean;

  /**
   * CSS class name for the error
   */
  className?: string;
}

export const FieldError = ({
  children,
  errorPrefix = 'Error:',
  fieldLabel,
  isInvalid,
  className,
  ...props
}: FieldErrorProps) => {
  // Separate style so we don't pass react-aria style function to a native span
  type StyleProp =
    | React.CSSProperties
    | ((...args: unknown[]) => React.CSSProperties);
  const { style, ...otherProps } = props as FieldErrorProps & {
    style?: StyleProp;
  };
  // For native span, only pass style if it's a plain CSSProperties object
  const spanProps =
    typeof style === 'function' ? otherProps : { ...otherProps, style };
  const fullErrorPrefix = fieldLabel
    ? `${fieldLabel} ${errorPrefix}`
    : errorPrefix;

  // If children is provided (external error message), render directly
  if (children && isInvalid) {
    return (
      <span
        className={[styles.fieldError, className].filter(Boolean).join(' ')}
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        {...spanProps}
      >
        <VisuallyHidden>{fullErrorPrefix}</VisuallyHidden>
        <Icon
          iconName="triangle-exclamation"
          iconPrefix="fas"
          aria-hidden="true"
        />
        {typeof children === 'function'
          ? children({
              isInvalid: true,
              validationErrors: [
                errorPrefix
                  ? `${errorPrefix} An error occurred.`
                  : 'An error occurred.',
              ],
              validationDetails: {
                valid: false,
                badInput: false,
                customError: false,
                patternMismatch: false,
                rangeOverflow: false,
                rangeUnderflow: false,
                stepMismatch: false,
                tooLong: false,
                tooShort: false,
                typeMismatch: false,
                valueMissing: false,
              },
            })
          : children}
      </span>
    );
  }

  // Otherwise, use React Aria's FieldError for internal validation
  return (
    <AriaFieldError {...props}>
      {({ validationErrors, isInvalid }) => {
        // Resolve children if it's a function
        const errorMessage =
          typeof children === 'function'
            ? children({
                isInvalid,
                validationErrors,
                validationDetails: {
                  badInput: false,
                  customError: false,
                  patternMismatch: false,
                  rangeOverflow: false,
                  rangeUnderflow: false,
                  stepMismatch: false,
                  tooLong: false,
                  tooShort: false,
                  typeMismatch: false,
                  valid: !isInvalid,
                  valueMissing: false,
                },
              })
            : children || validationErrors?.[0];

        if (!errorMessage || !isInvalid) return null;

        return (
          <span
            className={[styles.fieldError, className].filter(Boolean).join(' ')}
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            <VisuallyHidden>{fullErrorPrefix}</VisuallyHidden>
            <Icon
              iconName="triangle-exclamation"
              iconPrefix="fas"
              aria-hidden="true"
            />
            {errorMessage}
          </span>
        );
      }}
    </AriaFieldError>
  );
};

export default FieldError;
